import { useCallback, useEffect, useMemo, useRef } from 'react';

import { bindAll } from 'bind-event-listener';
import type { UnbindFn } from 'bind-event-listener';

import type { DocNode } from '@atlaskit/adf-schema';
import { INPUT_METHOD, INSERT_MEDIA_VIA } from '@atlaskit/editor-common/analytics';
import type { MediaProvider } from '@atlaskit/editor-common/provider-factory';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics';
import type { LocalIdPlugin } from '@atlaskit/editor-plugin-local-id';
import type { MediaPlugin } from '@atlaskit/editor-plugin-media';
import type { Schema } from '@atlaskit/editor-prosemirror/model';
import { NodeSelection } from '@atlaskit/editor-prosemirror/state';
import { akEditorDefaultLayoutWidth } from '@atlaskit/editor-shared-styles';
import { getMediaClient } from '@atlaskit/media-client-react';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { buildImageUrl } from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';
import { ExperienceName as RovoExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { fetchMauiPersonalReadToken } from '@atlassian/conversation-assistant-service/common/utils/maui-media-token';
import type { AIPlugin } from '@atlassian/editor-plugin-ai';
import type { AiStreamingOrchestratorPlugin } from '@atlassian/editor-plugin-ai-streaming-orchestrator';
import { getConfluenceEditorClientId } from '@atlassian/rovo-content-bridge-api-commands/clients/confluence-smart-create-editor-client';
import { ROVO_CHAT_CLIENT_ID } from '@atlassian/rovo-content-bridge-api-commands/clients/rovo-chat-client';
import type { ConfluenceUpdateImageOnPageData } from '@atlassian/rovo-content-bridge-api-commands/commands/confluence-update-page-command';
import {
	ContentDestroyCommand,
	ContentReadyCommand,
} from '@atlassian/rovo-content-bridge-api-commands/commands/content-lifecycle-commands';
import type {
	EditorInsertContentCommandProperties,
	EditorInsertContentPresentation,
} from '@atlassian/rovo-content-bridge-api-commands/commands/editor-insert-content-command';
import { sendAIFCExperienceTrackingToParent } from '@atlassian/rovo-content-bridge-api-experience-tracking-sender';
import type { RovoContentBridgeAPI } from '@atlassian/rovo-content-bridge-api/bridge-api';
import type { ClientId } from '@atlassian/rovo-content-bridge-api/client-types';
import { getBrowserBridgeAPI } from '@atlassian/rovo-content-bridge-api/singleton';

import type {
	PassThroughCommandProps,
	StreamAbortCallback,
	StreamCallback,
	StreamEndCallback,
	StreamErrorCallback,
	StreamRepairCallback,
	StreamStartCallback,
	StreamTimeoutCallback,
} from './command-handlers/command-handler';
import { CommandManager } from './command-manager';
import type { AddCommandHandlersFn } from './types/command-manager';
import type { AdditionalContext, ContentLocation } from './types/content';
import type { CreateMediaProviderFn, UploadImageErrorCallback } from './types/media';
import { useMediaImageProcessor } from './useMediaImageProcessor';
import type { MediaNode } from './useMediaImageProcessor';
import { log } from './utils/aifc-logging';
import { fireUploadAndInsertMediaErrorAnalyticsEvent } from './utils/analytics';

/**
 * Checks if the current environment is staging
 */
export const isStaging = (): boolean => {
	if (typeof window === 'undefined') {
		return false;
	}

	const host = window.location.host;
	return (
		host.includes('.stg.atlassian') ||
		host.includes('.stg-east.frontend.public.atl-paas.net') ||
		host.includes('jira-dev.com')
	);
};

const bindRovoBridgeTeardown = (teardown: () => void): UnbindFn => {
	const run = () => {
		/**
		 * @description We do NOT want to yield a return value here. It can cause certain unload
		 * handlers to display a window alert with "Are you sure you want to leave this page?".
		 * Specifically, see @see https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event,
		 * which is remarked to be a cancellable event by browsers.
		 */
		teardown();
		// Although unnecessary (the JS context is being destroyed), we remove the event listeners
		// to avoid any potential memory leaks due to hanging references - side effects can cause this.
		unbind();
	};

	// Below, we attempt to teardown on different event handlers which we know will trigger
	// on complete unmount of the document in this JS context. Note: Same-origin iframes are
	// indeed considered their own JS context here; just that they have access to other contexts.
	// Browsers report `unload` and `beforeunload` to be unreliable. Hence, we leverage a chain
	// of these events. See official MDN documentation for more (linked below).

	/**
	 * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event
	 * TODO - bring beforeunload back by consolidating handlers per discussion at -
	 * https://bitbucket.org/atlassian/atlassian-frontend-monorepo/pull-requests/207747/overview#comment-674441389
	 */
	// window.addEventListener('beforeunload', run, { once: true });

	/**
	 * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/unload_event
	 * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/pagehide_event
	 */
	const unbind = bindAll(window, [
		{
			type: 'unload',
			listener: run,
			options: { once: true },
		},
		{
			type: 'pagehide',
			listener: run,
			options: { once: true },
		},
	]);

	return unbind;
};

function useRovoBridgeLifecycle(
	props: UseEditorRovoBridgeProps & {
		clientId: ClientId;
		onBridgeReady?: () => void;
		rovoBridgeAPI: RovoContentBridgeAPI | null;
	},
) {
	const contentType = 'page';
	const { clientId, contentLocation, contentId, rovoBridgeAPI, onBridgeReady } = props;

	useEffect(() => {
		if (!rovoBridgeAPI) {
			return;
		}

		rovoBridgeAPI.ready(clientId);
		rovoBridgeAPI.send(ContentReadyCommand, {
			senderId: clientId,
			receiverId: ROVO_CHAT_CLIENT_ID,
			contentId,
			contentType,
			canProvideContext: true,
			contentContext: contentLocation,
		});

		// Fire after both sides of the bridge are ready and ContentReadyCommand
		// has been delivered synchronously to BridgeAPIHandlers.
		onBridgeReady?.();

		function rovoBridgeTeardown() {
			if (!rovoBridgeAPI) {
				return;
			}

			rovoBridgeAPI.send(ContentDestroyCommand, {
				senderId: clientId,
				receiverId: ROVO_CHAT_CLIENT_ID,
				contentId,
				contentType,
				contentContext: contentLocation,
			});
			rovoBridgeAPI.teardown(clientId);
		}

		const unbind = bindRovoBridgeTeardown(() => rovoBridgeTeardown());

		return () => {
			try {
				unbind();
				rovoBridgeTeardown();
				// TODO: EDITOR-123 - clear inactivity timeouts?
			} catch {
				// TODO: EDITOR-123 - handle errors; should go to Sentry
				// could be sent in future by navigation lockout
			}
		};
	}, [clientId, contentLocation, contentId, contentType, rovoBridgeAPI, onBridgeReady]);
}

export type UseEditorRovoBridgeProps = {
	addCommandHandlers?: AddCommandHandlersFn;
	additionalContext?: AdditionalContext;
	contentId: string;
	contentLocation: ContentLocation;
	createMediaProvider?: CreateMediaProviderFn;
	editorAPI:
		| PublicPluginAPI<
				[AnalyticsPlugin, AiStreamingOrchestratorPlugin, AIPlugin, LocalIdPlugin, MediaPlugin]
		  >
		| undefined
		| null;
	mediaProvider?: Promise<MediaProvider>;
	/** Called after rovoBridgeAPI.ready() and ContentReadyCommand have been sent,
	 * guaranteeing both sides of the bridge are ready to communicate. */
	onBridgeReady?: () => void;
	onInsertContent?: (payload: EditorInsertContentCommandProperties) => void;
	onStream?: StreamCallback;
	onStreamAbort?: StreamAbortCallback;
	onStreamEnd?: StreamEndCallback;
	onStreamError?: StreamErrorCallback;
	onStreamRepair?: StreamRepairCallback;
	onStreamStart?: StreamStartCallback;
	onStreamTimeout?: StreamTimeoutCallback;
	onUploadImageError?: UploadImageErrorCallback;
	useGenericEditorSkill?: boolean;
	userId?: string;
};

/**
 * React hook that manages the Rovo bridge connection for AI streaming functionality in the Atlassian Editor.
 *
 * This hook establishes a bidirectional communication bridge between the editor and Rovo chat,
 * enabling AI-powered content generation, editing, and streaming operations. It handles the complete
 * lifecycle of the bridge connection including setup, command registration, and cleanup.
 *
 * Typically this is required for use with the `aiStreamingOrchestratorPlugin` and the free gen prompt field
 *
 * @param props - Configuration properties for the Rovo bridge
 * @param props.userId - Optional user ID for the user editing the content
 * @param props.contentLocation - The location context of the content ('global' | 'staging-area')
 * @param props.contentId - Unique identifier for the content being edited
 * @param props.editorAPI - The editor's plugin API, must include AnalyticsPlugin and AiStreamingOrchestratorPlugin
 * @param props.useGenericEditorSkill - [Recommended] Optional to enable the generic editor skill for products that don't have a specific minion
 * @param props.additionalContext - Optional additional context to pass to ReadContentCommand
 * @param props.mediaProvider - Optional media provider
 * @param props.onBridgeReady - Optional callback invoked when the bridge is ready
 * @param props.createMediaProvider - Optional function to create a media provider
 * @param props.onStreamStart - Optional callback when AI streaming starts
 * @param props.onStream - Optional callback for each streaming chunk received
 * @param props.onStreamEnd - Optional callback when AI streaming completes successfully
 * @param props.onStreamAbort - Optional callback when AI streaming is aborted
 * @param props.onStreamTimeout - Optional callback when AI streaming times out
 * @param props.onStreamError - Optional callback when AI streaming encounters an error
 * @param props.onStreamRepair - Optional callback for AI stream repair operations
 * @param props.onUploadImageError - Optional callback to handle upload image errors
 * @param props.onInsertContent - Optional callback to handle insert content
 * @param props.addCommandHandlers - Optional function to add custom command handlers
 *
 * @example
 * ```tsx
 * import { useEditorRovoBridge } from '@atlassian/editor-rovo-bridge';
 *
 * function MyEditor() {
 *   const { preset, editorApi: editorAPI } = usePreset(...);
 *
 *   useEditorRovoBridge({
 *     userId: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
 *     contentLocation: 'global',
 *     contentId: 'page-123',
 *     editorAPI,
 *     useGenericEditorSkill: true,
 *     additionalContext: {
 *       isViewMode: true,
 *     },
 *     onStreamStart: ({ streamType, payload }) => {
 *       console.log('AI streaming started:', streamType);
 *     },
 *     onStream: ({ streamType, payload }) => {
 *       console.log('AI streaming data:', payload);
 *     },
 *     onStreamEnd: ({ streamType, adfDoc }) => {
 *       console.log('AI streaming completed with ADF:', adfDoc);
 *     },
 *     onStreamError: ({ error, streamType }) => {
 *       console.error('AI streaming error:', error);
 *     },
 *   });
 *
 *   return <ComposableEditor preset={preset} />;
 * }
 * ```
 */
export const useEditorRovoBridge = ({
	contentLocation,
	contentId,
	userId,
	editorAPI,
	useGenericEditorSkill,
	additionalContext,
	mediaProvider,
	createMediaProvider,
	onUploadImageError,
	onStreamStart,
	onStream,
	onStreamEnd,
	onStreamAbort,
	onStreamTimeout,
	onStreamError,
	onStreamRepair,
	onBridgeReady,
	onInsertContent,
	addCommandHandlers,
}: UseEditorRovoBridgeProps): void => {
	const product = 'confluence';
	const contentType = 'page';
	const clientId = getConfluenceEditorClientId(contentId);

	useEffect(() => {
		if (!editorAPI) {
			return;
		}
		editorAPI.aiExperience?.actions.updateTargetId(contentId);
	}, [editorAPI, contentId]);

	const rovoBridgeAPI = useMemo(() => {
		if (expValEquals('platform_editor_ai_disable_bridge_without_ai', 'isEnabled', true)) {
			if (!editorAPI || !editorAPI.aiExperience || !editorAPI.aiStreamingOrchestrator) {
				return null;
			}
		} else {
			if (!editorAPI) {
				return null;
			}
		}

		return getBrowserBridgeAPI();
	}, [editorAPI]);

	const imageProcessor = useMediaImageProcessor({
		mediaProvider,
		createMediaProvider,
		onUploadImageError,
	});

	const mediaProviderRef = useRef<Promise<MediaProvider> | undefined>(undefined);
	useEffect(() => {
		mediaProviderRef.current = mediaProvider;
	}, [mediaProvider]);

	const onPrepareImageUpdate: PassThroughCommandProps['onPrepareImageUpdate'] = useCallback(
		(previousMediaFileId: string) => {
			if (!fg('cc-maui-phase-2')) {
				return;
			}
			if (!expValEquals('cc-maui-experiment', 'isEnabled', true)) {
				return;
			}
			const mediaAPI = editorAPI?.media;
			if (!mediaAPI) {
				return;
			}

			editorAPI.core.actions.execute(mediaAPI.commands.setAIGenerating(previousMediaFileId));
		},
		[editorAPI],
	);

	const onClearAIGenerating: PassThroughCommandProps['onClearAIGenerating'] = useCallback(
		(previousMediaFileId: string) => {
			if (!fg('cc-maui-phase-2')) {
				return;
			}
			if (!expValEquals('cc-maui-experiment', 'isEnabled', true)) {
				return;
			}
			const mediaAPI = editorAPI?.media;
			if (!mediaAPI) {
				return;
			}

			editorAPI.core.actions.execute(mediaAPI.commands.clearAIGenerating(previousMediaFileId));
		},
		[editorAPI],
	);

	const onUploadAndInsertMedia: PassThroughCommandProps['onUploadAndInsertMedia'] = useCallback(
		async (
			sourceUrl: string,
			payload?: Partial<ConfluenceUpdateImageOnPageData>,
			_presentation?: EditorInsertContentPresentation,
		) => {
			if (!editorAPI || !editorAPI.media) {
				log('onUploadAndInsertMedia: Editor API or media plugin not available');
				return;
			}
			const resolvedProvider = await mediaProviderRef.current;
			if (
				!resolvedProvider?.uploadMediaClientConfig ||
				!resolvedProvider.uploadParams?.collection
			) {
				log('onUploadAndInsertMedia: No media provider available');
				return;
			}

			const { mediaFileId, previousMediaFileId, dynamicUiSource } = payload ?? {};
			const localId = dynamicUiSource?.startLocalId;

			try {
				const mediaClient = getMediaClient(resolvedProvider.uploadMediaClientConfig);
				const targetCollection = resolvedProvider.uploadParams.collection;

				if (previousMediaFileId && mediaFileId && userId) {
					const { token, collection } = await fetchMauiPersonalReadToken(product);
					const imageUrl = buildImageUrl({
						fileId: mediaFileId,
						token: token.split('Bearer ')[1],
						collection,
						clientId: userId,
						isStaging: isStaging(),
					});
					const result = await mediaClient.file.uploadExternal(imageUrl, targetCollection);
					const { id, occurrenceKey } = result.uploadableFileUpfrontIds;
					const { height, width } = result.dimensions;

					const mediaAttrs = {
						id,
						collection: targetCollection,
						occurrenceKey,
						type: 'file' as const,
						__fileMimeType: result.mimeType,
						width: akEditorDefaultLayoutWidth,
						height: Math.round(akEditorDefaultLayoutWidth * (height / width)),
					};

					// Replace existing media node and set AI generating decoration on the new node
					editorAPI.core.actions.execute(({ tr }) => {
						// Get the editor's schema
						const schema: Schema | undefined = editorAPI.core.sharedState.currentState()?.schema;
						if (!schema) {
							throw new Error('Could not get editor schema for replacing media node');
						}

						const { selection } = tr;
						let found = false;

						// Fast path: if the current selection is a NodeSelection on a mediaSingle
						// that contains the target media node, use that position directly.
						if (selection instanceof NodeSelection) {
							const selectedNode = selection.node;
							if (selectedNode.type === schema.nodes.mediaSingle) {
								const mediaNode = selectedNode.content.child(0);
								if (
									mediaNode &&
									mediaNode.attrs.id === previousMediaFileId &&
									(localId ? mediaNode.attrs.localId === localId : true)
								) {
									tr.setNodeMarkup(selection.from + 1, undefined, {
										...mediaNode.attrs,
										...mediaAttrs,
									});
									found = true;
								}
							}
						}

						// Slow path: traverse the whole document
						if (!found) {
							tr.doc.descendants((node, pos, parent) => {
								if (
									!found &&
									node.type === schema.nodes.media &&
									node.attrs.id === previousMediaFileId &&
									(localId ? parent?.attrs.localId === localId : true)
								) {
									tr.setNodeMarkup(pos, undefined, {
										...node.attrs,
										...mediaAttrs,
									});
									found = true;
									return false;
								}
							});
						}

						if (found) {
							tr.scrollIntoView();
						}

						return tr;
					});

					// Set AI generating decoration on the new media node
					if (editorAPI.media) {
						if (fg('cc-maui-phase-2')) {
							if (expValEquals('cc-maui-experiment', 'isEnabled', true)) {
								editorAPI.core.actions.execute(editorAPI.media.commands.setAIGenerating(id));
							}
						}
					}

					// Clear AI generating decoration after the upload completes
					const subscription = mediaClient.file.getFileState(id).subscribe({
						next: (fileState) => {
							if (fileState.status !== 'uploading') {
								if (editorAPI.media) {
									if (fg('cc-maui-phase-2')) {
										if (expValEquals('cc-maui-experiment', 'isEnabled', true)) {
											editorAPI.core.actions.execute(
												editorAPI.media.commands.clearAIGenerating(id),
											);
										}
									}
								}
								subscription.unsubscribe();
							}
						},
						error: () => subscription.unsubscribe(),
					});
				} else {
					const result = await mediaClient.file.uploadExternal(sourceUrl, targetCollection);
					const { id, occurrenceKey } = result.uploadableFileUpfrontIds;
					const { height, width } = result.dimensions;

					const mediaAttrs = {
						id,
						collection: targetCollection,
						occurrenceKey,
						type: 'file' as const,
						__fileMimeType: result.mimeType,
						width: akEditorDefaultLayoutWidth,
						height: Math.round(akEditorDefaultLayoutWidth * (height / width)),
					};

					// Insert new media node
					if (!editorAPI.media) {
						return;
					}
					const insertMediaSingleCommand = editorAPI.media.commands.insertMediaSingle(
						mediaAttrs,
						INPUT_METHOD.REMIX,
						INSERT_MEDIA_VIA.LOCAL_UPLOAD,
					);

					editorAPI.core.actions.execute(
						({ tr }) => insertMediaSingleCommand({ tr })?.scrollIntoView() ?? tr,
					);

					// Set AI generating decoration on the newly inserted node
					if (editorAPI.media) {
						if (fg('cc-maui-phase-2')) {
							if (expValEquals('cc-maui-experiment', 'isEnabled', true)) {
								editorAPI.core.actions.execute(editorAPI.media.commands.setAIGenerating(id));
							}
						}
					}

					// Clear AI generating decoration after the upload completes
					const subscription = mediaClient.file.getFileState(id).subscribe({
						next: (fileState) => {
							if (fileState.status !== 'uploading') {
								if (editorAPI.media) {
									if (fg('cc-maui-phase-2')) {
										if (expValEquals('cc-maui-experiment', 'isEnabled', true)) {
											editorAPI.core.actions.execute(
												editorAPI.media.commands.clearAIGenerating(id),
											);
										}
									}
								}
								subscription.unsubscribe();
							}
						},
						error: () => subscription.unsubscribe(),
					});
				}
			} catch (error) {
				log('onUploadAndInsertMedia: Error uploading media', { error });
				fireUploadAndInsertMediaErrorAnalyticsEvent(editorAPI, (error as Error).message);
			} finally {
				editorAPI.aiExperience?.actions.endAIExperience();
			}
		},
		[editorAPI, userId],
	);

	const reuploadImages: PassThroughCommandProps['reuploadImages'] = useCallback(
		async (adfDoc?: DocNode) => {
			const processImagesAndUpdateDocument = async () => {
				if (!imageProcessor?.processImagesInAdf) {
					throw new Error(
						'Image processor not available for image re-uploading in editor streaming',
					);
				}
				if (!editorAPI) {
					throw new Error('Editor API not available for image re-uploading in editor streaming');
				}

				if (fg('confluence_frontend_aifc_reupload_image')) {
					const editorDoc = await editorAPI.core.actions.requestDocumentAsync();
					if (!editorDoc) {
						throw new Error('Could not get document from editor for image re-uploading');
					}

					const { error, hasChanges, changedNodesByLocalId } =
						await imageProcessor.processImagesInAdf(editorDoc as DocNode);

					log('reuploadImages', { error, hasChanges, adfDoc, changedNodesByLocalId });

					if (error) {
						throw error;
					}

					if (hasChanges && changedNodesByLocalId && changedNodesByLocalId.size > 0) {
						// Get the editor's schema to convert ADF to ProseMirror nodes
						const schema: Schema | undefined = editorAPI.core.sharedState.currentState()?.schema;
						if (!schema) {
							throw new Error('Could not get editor schema for image re-uploading');
						}
						// Replace individual nodes
						changedNodesByLocalId.forEach((adfNode: MediaNode, localId: string) => {
							// Convert ADF to ProseMirror node
							const pmNode = schema.nodeFromJSON(adfNode);
							editorAPI?.localId?.actions.replaceNode({
								localId,
								value: pmNode,
							});
						});
					}
				} else {
					if (!adfDoc) {
						throw new Error('adfDoc is required for image re-uploading when feature flag is off');
					}
					const { error, hasChanges, processedAdf } =
						await imageProcessor.processImagesInAdf(adfDoc);

					log('reuploadImages', { error, hasChanges, processedAdf, adfDoc });

					if (error) {
						throw error;
					}

					if (hasChanges) {
						editorAPI?.core.actions.replaceDocument(processedAdf, {
							scrollIntoView: false,
						});
					}
				}
			};

			try {
				await processImagesAndUpdateDocument();
			} catch (error) {
				void sendAIFCExperienceTrackingToParent({
					status: 'fail',
					actionType: 'page',
					actionId: contentId,
					contentContext: contentLocation,
					experienceName: RovoExperienceName.AIFC_ALL_DATA_HANDLED,
					error: (error as Error).message,
				});

				onUploadImageError?.({ error: error as Error });
			}
		},
		[imageProcessor, editorAPI, contentId, contentLocation, onUploadImageError],
	);

	const commandProps = useMemo<PassThroughCommandProps | null>(() => {
		if (!editorAPI) {
			return null;
		}
		return {
			clientId,
			contentLocation,
			contentId,
			contentType,
			editorAPI,
			useGenericEditorSkill,
			additionalContext,
			onStreamStart,
			onStream,
			onStreamEnd,
			onStreamAbort,
			onStreamTimeout,
			onStreamError,
			onStreamRepair,
			onInsertContent,
			onPrepareImageUpdate,
			onClearAIGenerating,
			onUploadAndInsertMedia,
			reuploadImages,
		};
	}, [
		clientId,
		contentLocation,
		contentId,
		contentType,
		editorAPI,
		useGenericEditorSkill,
		additionalContext,
		onStreamStart,
		onStream,
		onStreamEnd,
		onStreamAbort,
		onStreamTimeout,
		onStreamError,
		onStreamRepair,
		onInsertContent,
		onPrepareImageUpdate,
		onClearAIGenerating,
		onUploadAndInsertMedia,
		reuploadImages,
	]);

	const commandManagerRef = useRef<CommandManager | null>(null);

	useEffect(() => {
		if (!editorAPI || !rovoBridgeAPI || !commandProps) {
			return;
		}

		// only initialize command manager once because
		// createCommandProcessor is stateful and doesn't support re-creation for now
		if (!commandManagerRef.current) {
			commandManagerRef.current = new CommandManager(
				clientId,
				rovoBridgeAPI,
				commandProps,
				addCommandHandlers,
			);
			commandManagerRef.current.addAndRegisterCommands();
		}
	}, [editorAPI, clientId, rovoBridgeAPI, commandProps, addCommandHandlers]);

	// clientId shouldn't change, but just in case
	// when it changes, we re-register the commands to the updated clientId
	useEffect(() => {
		if (!commandManagerRef.current || !rovoBridgeAPI) {
			return;
		}
		commandManagerRef.current.updateClientId(clientId, rovoBridgeAPI);
	}, [clientId, rovoBridgeAPI]);

	// when commandProps changes, we update the command props
	useEffect(() => {
		if (!commandManagerRef.current || !commandProps) {
			return;
		}
		commandManagerRef.current.updateCommandProps(commandProps);
	}, [commandProps]);

	// when addCommandHandlers changes, we deregister and register the commands
	useEffect(() => {
		if (!commandManagerRef.current) {
			return;
		}

		commandManagerRef.current.updateAddCommandHandlers(addCommandHandlers);
	}, [addCommandHandlers]);

	// when the component unmounts, we deregister the commands
	useEffect(() => {
		return () => {
			commandManagerRef.current?.deregisterCommands();
		};
	}, []);

	useRovoBridgeLifecycle({
		contentLocation,
		contentId,
		editorAPI,
		onStreamStart,
		onStream,
		onStreamEnd,
		onStreamAbort,
		onStreamTimeout,
		onStreamError,
		onStreamRepair,
		addCommandHandlers,
		clientId,
		rovoBridgeAPI,
		onBridgeReady,
	});
};
